// src/containers/Receptions/ReceptionList/components/ReceptionForm.tsx
import { useForm, Controller } from "react-hook-form";
import { CircularProgress, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { Badge, Button, Popin, Text } from "$components";
import OrderSelect from "$containers/Orders/forms/OrderSelect";
import OrderForm from "$containers/Orders/forms/OrderForm";
import FileUploader from "$components/FileUploader/FileUploader";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import { toLocalDateString } from "$utils/utils";
import useReception from "$hooks/useReception";
import useJStyles from "$hooks/useJStyles";
import useAuth from "$hooks/useAuth";
import { ProductQuantity, OrderStatus, SuborderType } from "$types";
import {
  CompanyServices,
  EntrepotService,
  OrderService,
  ReceptionService,
} from "$services";
import ReceptionStatus from "$types/ReceptionStatus";
import ProductsSection from "./ProductsSection";
import ReferencingForm from "../../Referencing/ReferencingForm";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";
import { parseStrapiFormat } from "$utils/strapi";
import ProviderSelect from "./ProviderSelect";
import WarehouseSelector from "$components/JSelectors/WarehouseSelector/WarehouseSelector";
import { theme } from "$utils/theme/bataillog";

const extractData = (dataObject) => {
  if (!dataObject) return [];
  if (Array.isArray(dataObject)) return dataObject;
  if (dataObject.data && Array.isArray(dataObject.data)) return dataObject.data;
  return [];
};

/** Resolve Mongo/Prisma id from a field that may be a string id or a populated object */
const getIdFromMaybePopulated = (v: any): string | null => {
  if (v == null || v === "") return null;
  if (typeof v === "object") {
    const id = v._id ?? v.id;
    return id != null && id !== "" ? String(id) : null;
  }
  return String(v);
};

/**
 * First order in multiselect chip order (matches OrderSelect `newValues`), with full API row from `fullOrders`.
 */
const getFirstSelectedOrderFull = (
  pickerValues: any[] | undefined,
  fullOrders: any[] | undefined,
  fallbackOrders: any[]
): any | null => {
  if (!pickerValues?.length) {
    return fallbackOrders[0] ?? null;
  }
  const fullList = fullOrders || [];
  const matched = pickerValues
    .filter(
      (nv) => !(nv?._id === "new" && nv?.inputValue) && Boolean(nv?.reference)
    )
    .map((nv) => fullList.find((o: any) => o?.reference === nv.reference))
    .find((full) => full != null);
  return matched ?? fallbackOrders[0] ?? null;
};

/** Copy the order's places onto a new reception. Transfer origin stays on warehouseOrigin. */
const applyOrderPlacesToReception = (
  target: any,
  order: any,
  isTransfer: boolean
) => {
  const whId = getIdFromMaybePopulated(order?.warehouse);
  const pvId = getIdFromMaybePopulated(order?.provider);
  const originId = getIdFromMaybePopulated(order?.warehouseOrigin);
  if (whId) target.warehouse = whId;
  if (pvId) target.provider = pvId;
  if (isTransfer) target.warehouseOrigin = originId || "";
};

// Utility function to get ordered quantity for a specific product
const getOrderedQuantityForProduct = (
  orders: any[],
  orderReference: string,
  suborderReference: string,
  productId: string
): number => {
  const order = orders.find((order) => order.reference === orderReference);
  if (!order) return 0;

  const suborder = (order.suborders || []).find(
    (suborder) => suborder.reference === suborderReference
  );
  if (!suborder) return 0;

  const product = (suborder.suborderProducts || []).find(
    (product) => product.reference === productId
  );
  if (!product) return 0;

  return Number(product.quantity) || 0;
};

// Utility function to get suborderProductId for a specific product
const getSuborderProductIdForProduct = (
  orders: any[],
  orderReference: string,
  suborderReference: string,
  productId: string
): number | undefined => {
  const order = orders.find((order) => order.reference === orderReference);
  if (!order) return undefined;

  const suborder = (order.suborders || []).find(
    (suborder) => suborder.reference === suborderReference
  );
  if (!suborder) return undefined;

  const product = (suborder.suborderProducts || []).find(
    (product) => product.reference === productId
  );
  return product?.id ?? product?._id ?? undefined;
};

// Function to initialize received quantities
const initializeReceivedQuantities = (
  formatedReception: any,
  setValue: (name: string, value: any) => void,
  isReadOnly: boolean
) => {
  if (!formatedReception || isReadOnly) return;

  // Only initialize for TODO status receptions
  if (formatedReception.status !== ReceptionStatus.TODO) return;

  const productQuantity = formatedReception.productQuantity || [];

  productQuantity.forEach((pq: any, index: number) => {
    // Only set default if receivedQuantity is 0 or null/undefined
    const currentReceived = Number(pq.receivedQuantity) || 0;

    if (currentReceived === 0) {
      const alreadyReceived = Number(pq.alreadyReceivedQuantity) || 0;

      // Find the corresponding product in orders to get the ordered quantity
      const orderedQuantity = getOrderedQuantityForProduct(
        formatedReception.orders,
        pq.orderReference,
        pq.suborderReference,
        pq.productId
      );

      // If nothing has been received yet, default to the full ordered quantity
      // If something has already been received, default to 0
      const defaultValue = alreadyReceived === 0 ? orderedQuantity : 0;

      if (defaultValue > 0) {
        setValue(
          `productQuantity[${index}].receivedQuantity`,
          defaultValue.toString()
        );
      }
    }
  });
};

export interface FieldValues {
  blNumber: string;
  company: string;
  files: any[];
  isReferenced: boolean;
  notes: string;
  orders: any[];
  productQuantity: ProductQuantity[];
  provider: string;
  receptionDate: string;
  reference: string;
  status: string;
  warehouse: string;
  warehouseReference: string;
  createdAt: string;
  warehouseOrigin: string;
}

type Props = {
  reception: any;
  onSubmit: (data: any) => Promise<any>;
  onReset: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
  isSaving?: boolean;
  isReadOnly?: boolean;
  fromOrderForm?: boolean;
};

// TODO : Set popin default values
const defaultValues = {
  reference: null,
  receptionDate: null,
  isReferenced: false,
  company: null,
  warehouse: null,
  warehouseReference: null,
  warehouseOrigin: null,
  blNumber: null,
  provider: null,
  files: [],
  status: OrderStatus.TO_DELIVERED,
  notes: "",
  orders: [
    {
      id: null,
      company: null,
      orderDate: null,
      provider: null,
      warehouse: null,
      reference: null,
      suborders: [
        {
          type: SuborderType.STOCK,
          company: null,
          project: null,
          suborderProducts: [
            {
              reference: null,
              name: null,
              unit: null,
              quantity: null,
              deliveryDate: null,
              status: OrderStatus.TO_DELIVERED,
            },
          ],
        },
      ],
    },
  ],
};

const cleanDataForAPI = (formData: any) => {
  const cleanedData = JSON.parse(JSON.stringify(formData));

  // Get all suborderProduct IDs
  const suborderProductIds = new Set();
  const ordersForCleanup = Array.isArray(cleanedData.orders)
    ? cleanedData.orders
    : [];
  ordersForCleanup.forEach((order) => {
    order?.suborders?.forEach((suborder) => {
      suborder.suborderProducts?.forEach((product) => {
        if (product.id) suborderProductIds.add(product.id);
      });
    });
  });

  const enrichQuantities = (list: any[] | undefined) => {
    if (!Array.isArray(list)) return list;
    return list.map((pq) => {
      // Remove conflicting productQuantity IDs
      const base =
        pq.id && suborderProductIds.has(pq.id)
          ? (({ id: _id, ...rest }) => rest)(pq)
          : pq;
      // Enrich with suborderProductId when possible
      if (
        base.suborderProductId === undefined ||
        base.suborderProductId === null
      ) {
        const suborderProductId = getSuborderProductIdForProduct(
          cleanedData.orders || [],
          base.orderReference,
          base.suborderReference,
          base.productId
        );
        if (suborderProductId !== undefined) {
          return { ...base, suborderProductId };
        }
      }
      return base;
    });
  };

  // Apply to both payload shapes (backend may read either key depending on code path)
  cleanedData.productQuantity =
    enrichQuantities(cleanedData.productQuantity) || [];
  if (cleanedData.productQuantities !== undefined) {
    cleanedData.productQuantities = enrichQuantities(
      cleanedData.productQuantities
    );
  }

  return cleanedData;
};

function ReceptionForm({
  reception: receptionData,
  onSubmit,
  onReset,
  onDelete,
  canDelete = true,
  isSaving,
  isReadOnly = false,
  fromOrderForm = false,
}: Props) {
  const { classes, referencingOpen, toggleReferencingPopin } = useReception();
  const commonClasses = useCommonStyles();
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderNewValue, setOrderNewValue] = useState(undefined);
  const { jclasses } = useJStyles();
  const { isAdmin } = useAuth();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [companies, setCompanies] = useState([]);
  const [warehouse, setWarehouse] = useState([]);

  const reception = receptionData;

  // Query Companies
  const fecthCompanies = useCallback(async () => {
    const filters = {
      page: 1,
      rowsPerPage: 50000,
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        staleTime: 60 * 1000,
      });
      // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
      companies = companies?.data || companies || [];
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  // Update the formatedReception useMemo to correctly transform the reception data
  const formatedReception = useMemo(() => {
    if (!reception) {
      return reception;
    }

    // Use parseStrapiFormat to clean the entire reception object from Strapi structure
    const newReception = parseStrapiFormat(reception);

    // Clean basic structure
    newReception.files = extractData(newReception.files);

    // Normalize API variants so the form always edits `productQuantity`.
    // Some backend paths return `productQuantities` (plural), while the form watches `productQuantity`.
    if (
      (newReception.productQuantity == null ||
        (Array.isArray(newReception.productQuantity) &&
          newReception.productQuantity.length === 0)) &&
      Array.isArray(newReception.productQuantities) &&
      newReception.productQuantities.length > 0
    ) {
      newReception.productQuantity = newReception.productQuantities;
    }

    // Orders are now already cleaned by parseStrapiFormat, but ensure they're in the right format
    const ordersArray = extractData(newReception.orders);

    // Build a set of suborder references that are part of this reception's productQuantity.
    // This is used to filter out suborders that were added to the order AFTER the reception
    // was created, preventing them from being shown/included in this reception.
    const receptionProductQuantity =
      newReception.productQuantity || newReception.productQuantities || [];

    newReception.orders = ordersArray.map((order) => {
      // Collect suborder references from productQuantity for this specific order
      const receptionSuborderRefs = new Set(
        receptionProductQuantity
          .filter((pq: any) => pq.orderReference === order.reference)
          .map((pq: any) => pq.suborderReference)
          .filter(Boolean)
      );

      // If we have productQuantity entries, only show suborders that are referenced in them.
      // Otherwise (new reception), show all suborders.
      const filteredSuborders =
        receptionSuborderRefs.size > 0
          ? (order.suborders || []).filter((s: any) =>
              receptionSuborderRefs.has(s.reference)
            )
          : order.suborders || [];

      return {
        ...order,
        id: order.id || order._id,
        files: extractData(order.files),
        suborders: filteredSuborders,
        reference: order.reference,
        company: order.company,
        createdAt: order.createdAt || new Date().toISOString(),
      };
    });

    // DON'T modify productQuantity here - use server data as-is
    // The conflicts will be resolved in cleanDataForAPI before submission

    return newReception;
  }, [reception]);

  const companiesOptions = companies?.filter(
    ({ _id }) => _id === reception?.company
  );

  const formMethods = useForm<FieldValues>({
    mode: "onChange",
    defaultValues: formatedReception || defaultValues,
  });

  const {
    control,
    getValues,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = formMethods;

  // Reset only when opening another reception. The list replaces the open
  // row on every refetch; resetting then wipes the draft (including a file
  // just uploaded) before Enregistrer can send it.
  const lastResetKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!formatedReception) return;
    const resetKey =
      formatedReception.id != null
        ? String(formatedReception.id)
        : formatedReception.isTransfer
          ? "new-transfer"
          : "new";
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;
    reset(formatedReception);
  }, [formatedReception, reset]);

  const orders = watch("orders") as any;
  const warehouseValue = watch("warehouse");
  const warehouseOriginValue = watch("warehouseOrigin");

  // get warehouses
  const fecthWarehouses = useCallback(async () => {
    const listWarehouseId = [warehouseValue, warehouseOriginValue].filter(
      Boolean
    );
    if (listWarehouseId.length > 0) {
      const filters = {
        $inId: listWarehouseId,
      };
      const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);
      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => EntrepotService.getEntrepots(filters),
          gcTime: 5 * 60 * 1000,
        });
        queryClient.setQueryData(queryKey, getWarehouses);
      }
      // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
      setWarehouse(getWarehouses?.data || getWarehouses || []);
    }
  }, [warehouseOriginValue, warehouseValue]);

  useEffect(() => {
    fecthWarehouses();
  }, [fecthWarehouses]);

  // In ReceptionForm.tsx - Replace the onSubmitOrder function with this:

  const onSubmitOrder = useCallback(
    async (data: any) => {
      const order = { ...data };
      delete order?.id;

      const res: any = await OrderService.createOrderItem(order);
      const currentReception = getValues();
      const newReception = {
        ...currentReception,
        productQuantity: [...currentReception.productQuantity],
      };
      newReception.orders = [{ ...order, id: res?.id }];

      newReception.orders.forEach((order: any) => {
        order.suborders.forEach((suborder: any) => {
          suborder.suborderProducts.forEach((suborderProduct: any) => {
            const existingQuantityIndex =
              newReception.productQuantity.findIndex(
                (q: any) =>
                  q.orderReference === order.reference &&
                  q.suborderReference === suborder.reference &&
                  q.productId === suborderProduct.reference
              );

            if (existingQuantityIndex === -1) {
              // CRITICAL: Add WITHOUT any ID field
              newReception.productQuantity.push({
                orderReference: order.reference,
                suborderReference: suborder.reference,
                productId: suborderProduct.reference,
                suborderProductId: suborderProduct.id,
                receivedQuantity:
                  reception.status === ReceptionStatus.TODO
                    ? suborderProduct.quantity
                    : 0,
                alreadyReceivedQuantity: 0,
                unconformQuantity: 0,
                conformity: true,
              });
            }
          });
        });
      });

      if (!reception?.id && newReception.orders[0]) {
        applyOrderPlacesToReception(
          newReception,
          newReception.orders[0],
          Boolean(reception?.isTransfer)
        );
      }

      setValue("orders", newReception.orders);
      setValue("productQuantity", newReception.productQuantity);
      setValue("warehouse", newReception.warehouse);
      setValue("warehouseOrigin", newReception.warehouseOrigin);
      setValue("provider", newReception.provider, { shouldValidate: true });

      setShowOrderModal(false);
      setOrderNewValue(undefined);
    },
    [
      getValues,
      reception?.id,
      reception?.isTransfer,
      reception.status,
      setValue,
    ]
  );

  // Also update the ordersData useMemo to be consistent
  const ordersData = useMemo(() => {
    if (!orders) {
      return [];
    }

    // Use parseStrapiFormat to clean the orders data from Strapi structure
    const cleanedOrders = parseStrapiFormat(orders);
    const ordersArray = extractData(cleanedOrders);

    return ordersArray.map((order) => {
      return {
        ...order,
        // Ensure ID is preserved
        id: order.id || order._id,
        // Ensure files is always an array
        files: extractData(order.files),
        // Ensure reference is always available
        reference: order.reference || "",
        // Ensure company is preserved
        company: order.company,
        // Ensure createdAt is preserved
        createdAt: order.createdAt || new Date().toISOString(),
      };
    });
  }, [orders]);

  const isCreate = !reception?.id;

  // Referencing popin save must bypass the reception-form mandatory-fields guard.
  // It only submits `{ id, receptionReferences }`.
  const onSubmitReferencing = async (data: any) => {
    const id = data?.id ?? formatedReception?.id;
    if (!id) return;
    return ReceptionService.updateReceptionItem(String(id), {
      receptionReferences: data?.receptionReferences ?? [],
    });
  };

  return (
    <div className={jclasses.jWrapper}>
      <form
        noValidate
        onSubmit={handleSubmit(
          async (data) => {
            setSaveError(null);
            const payload = {
              ...data,
              receptionDate: data.receptionDate || reception?.receptionDate,
              provider: data.provider || reception?.provider,
              company: data.company || reception?.company,
              warehouse: data.warehouse || reception?.warehouse,
              warehouseOrigin:
                data.warehouseOrigin || reception?.warehouseOrigin,
            };
            try {
              const cleanedData = cleanDataForAPI(payload);
              const saved = await onSubmit(cleanedData);
              if (saved === false) {
                setSaveError("L'enregistrement n'a pas abouti.");
              }
            } catch {
              setSaveError("L'enregistrement a été interrompu.");
            }
          },
          () => {
            setSaveError("Des champs invalides empêchent l'enregistrement.");
          }
        )}
      >
        <div className={classes.formTop}>
          <Text type="h2">
            {isCreate
              ? "Ajouter une réception"
              : isReadOnly
                ? `Détails de la réception ${reception?.reference}`
                : `Éditer la réception ${reception?.reference}`}
          </Text>
          {isReadOnly && <Badge color="orange">Lecture seule</Badge>}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {reception?.id && !isReadOnly && (
              <Button
                variant="text"
                onClick={onDelete}
                disabled={!canDelete}
                confirm
                confirmMessage={`Supprimer la réception ${reception?.reference} ?`}
                style={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  padding: 0,
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
                title={
                  !canDelete
                    ? "Impossible de supprimer : la réception est utilisée dans une EDB"
                    : "Supprimer la réception"
                }
                sx={{
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    boxShadow: "none",
                  },
                  "& svg": {
                    color: canDelete ? "black" : "#B3BAC5",
                  },
                }}
              >
                <DeleteBin2LineIcon style={{ height: "100%", width: "100%" }} />
              </Button>
            )}
          </div>
        </div>

        <div className={jclasses.formSectionHead}>
          <Text type="h3">Informations réception</Text>
        </div>
        <div className={classes.fieldsRow}>
          {/* Id réception (édition uniquement) */}
          {!isCreate && (
            <div className={classes.fieldsColIdReception}>
              <Controller
                control={control}
                name="reference"
                render={({ field: { value } }) => (
                  <TextField
                    id="reference"
                    label="Id réception"
                    variant="outlined"
                    value={value}
                    disabled
                    fullWidth
                  />
                )}
              />
            </div>
          )}

          {/* Date - date picker */}
          <div className={classes.fieldsColDate}>
            <Controller
              control={control}
              name="receptionDate"
              rules={
                isCreate
                  ? {
                      required: "Veuillez remplir ce champ",
                    }
                  : undefined
              }
              render={({ field: { value } }) =>
                isCreate ? (
                  <BataillogDatePicker
                    selectedDate={value ? new Date(value) : undefined}
                    onDateChange={(dates) => {
                      const d = Array.isArray(dates) ? dates[0] : dates;
                      setValue(
                        "receptionDate",
                        d instanceof Date ? toLocalDateString(d) : null,
                        {
                          shouldValidate: true,
                        }
                      );
                    }}
                    label="Date de réception"
                    error={errors.receptionDate as any}
                    variant="outlined"
                    style={{ width: "100%" }}
                  />
                ) : (
                  <TextField
                    id="Date de réception"
                    name="receptionDate"
                    label="Date de réception"
                    variant="outlined"
                    value={new Date(value).toLocaleDateString()}
                    disabled
                    fullWidth
                  />
                )
              }
            />
          </div>

          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="orders"
              rules={
                isCreate
                  ? {
                      validate: (value: any) => {
                        const arr =
                          value && value.data
                            ? value.data
                            : Array.isArray(value)
                              ? value
                              : [];
                        return arr.length > 0 || "Veuillez remplir ce champ";
                      },
                    }
                  : undefined
              }
              render={({ field: { value } }: any) => {
                // Ensure value is an array we can map over
                const ordersArray =
                  value && value.data
                    ? value.data
                    : Array.isArray(value)
                      ? value
                      : [];

                return fromOrderForm ||
                  getValues("status") !== ReceptionStatus.TODO ? (
                  <TextField
                    id="commandRefs"
                    name="commandRefs"
                    label="N° de commande(s)"
                    variant="outlined"
                    value={ordersArray
                      .map((order: any) => {
                        // Use Prisma format (direct properties)
                        return order?.reference || order;
                      })
                      .join(", ")}
                    disabled
                    fullWidth
                  />
                ) : (
                  <div style={{ width: "100%" }}>
                    <p
                      style={{
                        color: errors.orders
                          ? theme.color.red
                          : "rgba(0, 0, 0, 0.6)",
                        fontSize: 14,
                        marginTop: 0,
                        marginBottom: 6,
                      }}
                    >
                      N° de commande(s)
                    </p>
                    <OrderSelect
                      companies={companiesOptions}
                      displayCreateOrderOption={isAdmin()}
                      isTransfer={Boolean(reception?.isTransfer)}
                      value={ordersArray.map((order: any) => {
                        return {
                          _id: order?.id || order?._id,
                          reference: order?.reference,
                          company: order?.company,
                          createdAt:
                            order?.createdAt || new Date().toISOString(),
                        };
                      })}
                      onChange={async (newValues: any, newOrders: any) => {
                        // Handle "Create New Order" case
                        if (newValues?.slice(-1)?.[0]?.inputValue) {
                          setShowOrderModal(true);
                          setOrderNewValue(
                            newValues?.slice(-1)?.[0]?.inputValue
                          );
                          return;
                        }
                        const currentReception = getValues();
                        let newReception = {
                          ...currentReception,
                          productQuantity: [
                            ...(currentReception.productQuantity || []),
                          ],
                        };

                        // Get references from the new selection
                        const selectedRefs = new Set(
                          newValues.map(
                            (val: any) => val.reference || val.inputValue
                          )
                        );

                        // Get the current orders in a standard format
                        const currentOrdersArray = extractData(
                          newReception.orders
                        );

                        // Filter current orders to keep only those that are still selected
                        const remainingOrders = currentOrdersArray.filter(
                          (order: any) => {
                            const orderRef = order?.reference;
                            return selectedRefs.has(orderRef);
                          }
                        );

                        // If we have new orders to add (not just removing)
                        if (newOrders && newOrders.length > 0) {
                          // Create a set of existing references to prevent duplicates
                          const existingRefs = new Set(
                            remainingOrders.map(
                              (order: any) => order?.reference
                            )
                          );

                          // Only add orders that don't already exist
                          const ordersToAdd = newOrders.filter(
                            (newOrder: any) => {
                              const orderRef = newOrder?.reference;
                              return !existingRefs.has(orderRef);
                            }
                          );

                          // Combine remaining orders with new ones
                          newReception.orders = [
                            ...remainingOrders,
                            ...ordersToAdd,
                          ];
                        } else {
                          // Just use the filtered orders (deletion case)
                          newReception.orders = remainingOrders;
                        }

                        // Set company from the first order if not already set
                        if (
                          !newReception.company &&
                          newReception.orders.length > 0
                        ) {
                          // Order `company` can be populated (object) or a string id
                          const companyId = getIdFromMaybePopulated(
                            newReception.orders[0].company
                          );
                          newReception.company =
                            companyId || newReception.orders[0].company;
                        }

                        // Ensure company is always an id string before calling the quantity API
                        newReception.company =
                          getIdFromMaybePopulated(newReception.company) ||
                          newReception.company;

                        // Calculate quantities (GET route): backend fetches orders/receptions from DB.
                        const computed =
                          await ReceptionService.getReceptionQuantityForOrders({
                            company: newReception.company,
                            orderIds: (newReception.orders || [])
                              .map((o: any) => o?.id || o?._id)
                              .filter(Boolean),
                          });

                        // Merge: keep user-entered fields from current productQuantity, refresh alreadyReceivedQuantity.
                        const existingByKey = new Map<string, any>();
                        (newReception.productQuantity || []).forEach(
                          (pq: any) => {
                            const k = `${pq?.orderReference}|${pq?.suborderReference}|${pq?.productId}`;
                            existingByKey.set(k, pq);
                          }
                        );

                        const mergedProductQuantity = (
                          (computed as any)?.productQuantity || []
                        ).map((pq: any) => {
                          const k = `${pq?.orderReference}|${pq?.suborderReference}|${pq?.productId}`;
                          const existing = existingByKey.get(k);
                          return existing
                            ? {
                                ...pq,
                                receivedQuantity: existing.receivedQuantity,
                                unconformQuantity: existing.unconformQuantity,
                                conformity: existing.conformity,
                              }
                            : pq;
                        });

                        newReception = {
                          ...newReception,
                          productQuantity: mergedProductQuantity,
                        };

                        // Creation: entrepôt + fournisseur from first selected order (chip order)
                        if (!reception?.id) {
                          if (newReception.orders?.length > 0) {
                            const firstOrder = getFirstSelectedOrderFull(
                              newValues,
                              newOrders,
                              newReception.orders
                            );
                            if (firstOrder) {
                              applyOrderPlacesToReception(
                                newReception,
                                firstOrder,
                                Boolean(reception?.isTransfer)
                              );
                            }
                          } else {
                            newReception.warehouse = "";
                            newReception.provider = "";
                            if (reception?.isTransfer) {
                              newReception.warehouseOrigin = "";
                            }
                          }
                        }

                        // Update form values
                        setValue("orders", newReception.orders, {
                          shouldValidate: true,
                        });
                        setValue("warehouse", newReception.warehouse, {
                          shouldValidate: true,
                        });
                        setValue("company", newReception.company, {
                          shouldValidate: true,
                        });
                        setValue("provider", newReception.provider, {
                          shouldValidate: true,
                        });
                        setValue(
                          "warehouseOrigin",
                          newReception.warehouseOrigin,
                          { shouldValidate: true }
                        );
                        setValue(
                          "productQuantity",
                          newReception.productQuantity
                        );

                        initializeReceivedQuantities(
                          newReception,
                          setValue,
                          isReadOnly
                        );
                      }}
                    />
                  </div>
                );
              }}
            />
          </div>
        </div>
        <div className={classes.fieldsRow}>
          {/* Entreprise - select */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="company"
              render={({ field: { value } }) => (
                <TextField
                  id="company"
                  name="company"
                  label="Entreprise"
                  variant="outlined"
                  value={companies?.find((c: any) => c._id === value)?.name}
                  disabled
                  fullWidth
                />
              )}
            />
          </div>
          {/* Entrepot de réception - select */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="warehouse"
              rules={
                isCreate
                  ? {
                      required: "Veuillez remplir ce champ",
                    }
                  : undefined
              }
              render={({ field: { value } }) =>
                isCreate && !isReadOnly ? (
                  <WarehouseSelector
                    label="Entrepôt de réception"
                    parentFilter={String(getValues("company") || "")}
                    parentFilterRequired
                    hideError
                    onChange={(_e, data) => {
                      setValue("warehouse", data?._id || "", {
                        shouldValidate: true,
                      });
                    }}
                    value={warehouse?.find((w: any) => w._id === value) ?? null}
                    disableClearable
                    disabled={!getValues("company")}
                    placeholder="Entrepôt de réception"
                  />
                ) : (
                  <TextField
                    id="warehouse"
                    name="warehouse"
                    label="Entrepôt de réception"
                    variant="outlined"
                    value={warehouse?.find((c: any) => c._id === value)?.name}
                    disabled
                    fullWidth
                  />
                )
              }
            />
          </div>

          {/* Réference de l'entrepot de réception - select */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="warehouseReference"
              render={({ field }) => (
                <TextField
                  id="warehouseReference"
                  name="warehouseReference"
                  label="ID réception entrepôt"
                  variant="outlined"
                  {...field}
                  disabled={isReadOnly}
                  fullWidth
                />
              )}
            />
          </div>
        </div>
        <div className={classes.fieldsRow}>
          {/* Fournisseur - string */}
          <div className={classes.fieldsCol}>
            {reception?.isTransfer ? (
              <Controller
                control={control}
                name="warehouseOrigin"
                render={({ field: { value } }) => (
                  <TextField
                    id="warehouseOrigin"
                    name="warehouseOrigin"
                    label="Fournisseur / Entrepôt d'origine"
                    variant="outlined"
                    value={warehouse?.find((c: any) => c._id === value)?.name}
                    disabled
                    fullWidth
                  />
                )}
              />
            ) : (
              <Controller
                control={control}
                name="provider"
                rules={
                  isCreate
                    ? {
                        required: "Veuillez remplir ce champ",
                      }
                    : undefined
                }
                render={({ field: { value } }) =>
                  isCreate && !isReadOnly ? (
                    <ProviderSelect
                      value={value}
                      companyId={String(getValues("company") || "")}
                      label="Fournisseur de réception"
                      onChange={(providerId) => {
                        const pid = Array.isArray(providerId)
                          ? providerId[0]
                          : providerId;
                        setValue("provider", (pid || null) as any, {
                          shouldValidate: true,
                        });
                      }}
                      onBlur={() => {}}
                      error={!!errors.provider}
                    />
                  ) : (
                    <TextField
                      id="provider"
                      name="provider"
                      label="Fournisseur de réception"
                      variant="outlined"
                      value={companies?.find(({ _id }) => value === _id)?.name}
                      disabled
                      fullWidth
                    />
                  )
                }
              />
            )}
          </div>

          {/* N° BL, modifiable */}
          <div className={classes.fieldsCol}>
            <Controller
              control={control}
              name="blNumber"
              render={({ field }) => (
                <TextField
                  label="N° de bon de livraison"
                  variant="outlined"
                  {...field}
                  disabled={isReadOnly}
                  fullWidth
                />
              )}
            />
          </div>
          <div className={classes.fieldsCol} />
        </div>

        {!isCreate && (
          <>
            <div className={jclasses.formSectionHead}>
              <Text type="h3">Pièces jointes</Text>
            </div>
            <Controller
              control={control}
              name="files"
              render={({ field: { onChange, value } }) => {
                // Ensure value is always an array
                const safeValue = extractData(value);

                return (
                  <FileUploader
                    isReadOnly={isReadOnly || !isAdmin()}
                    value={safeValue}
                    onChange={onChange}
                    isAdmin={isAdmin()}
                    isStrapi
                    isSplitted
                    mainLabel="Pièce(s) jointe(s) de la réception :"
                    secondaryLabel="Pièce(s) jointe(s) des commandes :"
                    secondaryValue={ordersData || []}
                    mainEmptyLabel="Aucune pièce jointe pour la réception"
                    secondaryEmptyLabel="Aucune pièce jointe pour cette commande"
                  />
                );
              }}
            />
          </>
        )}
        <ProductsSection
          {...{
            control,
            register,
            getValues,
            setValue,
            errors,
            watch,
            reception,
            isReadOnly: isReadOnly || !isAdmin(),
          }}
        />

        <div className={jclasses.formSectionHead}>
          <Text type="h3">Notes supplémentaires</Text>
        </div>
        <Controller
          control={control}
          name="notes"
          render={({ field: { value } }) => (
            <TextField
              id="notes"
              variant="outlined"
              multiline
              minRows={3}
              fullWidth
              value={value}
              onChange={(e) => setValue("notes", e.target.value)}
              disabled={isReadOnly || !isAdmin()}
            />
          )}
        />
        {saveError && (
          <Text type="text" style={{ color: "#cc3300", marginTop: 12 }}>
            {saveError}
          </Text>
        )}
        {isAdmin() && (
          <div className={classes.footerForm}>
            <Button color="secondary" onClick={onReset}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress
                  style={{ width: 18, height: 18, color: "#FFFFFF" }}
                />
              ) : isCreate ? (
                "Créer la réception"
              ) : (
                "Enregistrer"
              )}
            </Button>
          </div>
        )}

        {reception?.status === ReceptionStatus.DONE && isAdmin() && (
          <div className={clsx(classes.footerForm, classes.secondFooterForm)}>
            <Button
              color="secondary"
              disabled={isSaving}
              onClick={() => toggleReferencingPopin(true)}
            >
              Gérer le référencement
            </Button>
          </div>
        )}
      </form>

      <Popin openPopin={referencingOpen} toggleDrawer={toggleReferencingPopin}>
        <Text type="h2">Référencement</Text>
        {referencingOpen && (
          <ReferencingForm
            isSavingReception={isSaving}
            onSubmit={onSubmitReferencing}
            receptionId={formatedReception.id}
            // reception={formatedReception}
            togglePopin={toggleReferencingPopin}
            isReadOnly={isReadOnly}
            value={warehouse?.find((c: any) => c._id === warehouseValue)?.name}
          />
        )}
      </Popin>
      <Popin
        customCloseAction={() => {
          setOrderNewValue(undefined);
        }}
        className={commonClasses.ProjectsPage_Popin}
        openPopin={showOrderModal}
        toggleDrawer={setShowOrderModal}
        minWidth="70%"
      >
        {showOrderModal && (
          <OrderForm
            order={null}
            isTransfer={Boolean(reception?.isTransfer)}
            orderNewValue={orderNewValue}
            onSubmit={onSubmitOrder}
            onCancelOrder={() => {
              setShowOrderModal(false);
              setOrderNewValue(undefined);
            }}
            onReset={() => {
              setShowOrderModal(false);
              setOrderNewValue(undefined);
            }}
          />
        )}
      </Popin>
    </div>
  );
}

export default ReceptionForm;
